import { queryOptions } from '@tanstack/react-query';
import type { ListProductsInput } from '@marxbiotech/shared-types/bff';
import type { HttpTypes } from '@marxbiotech/shared-types/medusa';

import { api } from '@lib/api/client';
import { CACHE_TIMES } from '@lib/query/cache-times';

// =============================================================================
// API Response Types
// =============================================================================

interface ProductListResponse {
  products: HttpTypes.StoreProduct[];
  count: number;
  offset: number;
  limit: number;
}

interface ProductResponse {
  product: HttpTypes.StoreProduct | null;
}

// =============================================================================
// Query Parameters
// =============================================================================

/**
 * Client-side product list params.
 * Extends shared ListProductsInput to allow single string for category_id/etc
 * (the BFF normalizes these to arrays).
 */
export interface ListProductsParams extends Omit<ListProductsInput, 'category_id' | 'collection_id' | 'tag_id'> {
  category_id?: string | string[];
  collection_id?: string | string[];
  tag_id?: string | string[];
}

// =============================================================================
// Query Keys
// =============================================================================

export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (params: ListProductsParams) => [...productKeys.lists(), params] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (handle: string, regionId: string) => [...productKeys.details(), handle, regionId] as const,
};

// =============================================================================
// Query Options
// =============================================================================

export const listProductsQueryOptions = (params: ListProductsParams) =>
  queryOptions({
    queryKey: productKeys.list(params),
    queryFn: async () => {
      const response = await api.get<ProductListResponse>('/products', {
        params,
      });
      return response.data;
    },
    staleTime: CACHE_TIMES.short.staleTime,
    gcTime: CACHE_TIMES.short.gcTime,
  });

export const productByHandleQueryOptions = (handle: string, regionId: string) =>
  queryOptions({
    queryKey: productKeys.detail(handle, regionId),
    queryFn: async () => {
      const response = await api.get<ProductResponse>(
        `/products/by-handle/${handle}`,
        { params: { region_id: regionId } }
      );
      return response.data.product;
    },
    staleTime: CACHE_TIMES.short.staleTime,
    gcTime: CACHE_TIMES.short.gcTime,
  });
